import React from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import {
    LocationProp,
    LocationType,
    EntryPoint,
    LegType,
    LegTypeProps,
    SourceOptionsType,
    TabTypeProp,
    TabTypePropDefault,
    GeoType,
} from '../../models';
import { StringUtils, CMS, withPageContext, TrackingUtils, ABUtils } from '../../utils';

import { DropdownItem } from '../../components/DropdownItem/DropdownItem';

const icons = {
    city: 'odf-icon-city',
    airplane: 'odf-icon-flight-right',
    train: 'odf-icon-train-right',
    location: 'odf-icon-location',
    hotel: 'odf-icon-bed-double',
    subway: 'odf-icon-subway',
    point_of_interest: 'odf-icon-museum',
};

const LocationItem = ({
    activeProduct,
    isSelected,
    location,
    parentLocation,
    displayedWord,
    onClickItem,
    isFixedWidth,
    departureOrArrival,
    trackEvent
}) => {
    const { isCssVariationMobile } = ABUtils.getABs();
    const hasIATABadge = isCssVariationMobile;

    const isLocationType = (location, types) => types.includes(location.type);
    const isStation = (location) => {
        const stationTypes = [LocationType.TRAIN_STATION, LocationType.METRO_STATION, LocationType.BUS_STATION];
        return isLocationType(location, stationTypes) || stationTypes.includes(location.regionType);
    };
    const isAirport = (location) => location.type === LocationType.AIRPORT || location.regionType === LocationType.AIRPORT;
    const isAirportOrTrainLocation = (parentLocation, location) => {
        return parentLocation || location.regionType === LocationType.AIRPORT;
    };
    const isHotelOrAccommodation = (location) =>
        isLocationType(location, [LocationType.HOTEL]) || location.type === GeoType.ACCOMMODATION;

    const isPointOfInterest = (location) => location.type === LocationType.REGION && location.regionType === LocationType.POINT_OF_INTEREST;

    const isTrains = activeProduct === EntryPoint.TRAINS;

    const isCityLocation = (location) => {
        return (
            [
                LocationType.CONTINENT,
                LocationType.COUNTRY,
                LocationType.STATE,
                LocationType.REGION,
                LocationType.MULTI_CITY,
                LocationType.CITY,
                LocationType.NEIGHBORHOOD,
            ].includes(location.type) ||
            (location.type === LocationType.IATA_CODE && !isEmpty(location.relatedLocations))
        );
    };

    const getItemIcon = () => {
        if (isAirportOrTrainLocation(parentLocation, location)) {
            return isAirport(location) ? icons.airplane : icons.train;
        } else if (isPointOfInterest(location)) {
            return icons.point_of_interest;
        } else if (isStation(location)) {
            return icons.subway;
        } else if (isCityLocation(location)) {
            return activeProduct === EntryPoint.HOTELS ? icons.location : icons.city;
        } else if (isLocationType(location, [LocationType.LANDMARK, LocationType.OTHERS])) {
            return icons.location;
        } else if (isHotelOrAccommodation(location)) {
            return icons.hotel;
        } else {
            return icons.airplane;
        }
    };

    const highlightClassNames = className({
        'odf-text-primary': isCssVariationMobile,
        'odf-bold odf-dropdown-highlight': !isCssVariationMobile,
    });

    const getHighlightedString = (initial, toHighlight, end) => {
        const highlighted = `<span class="${highlightClassNames}">${toHighlight}</span>`;
        return `${initial}${highlighted}${end}`;
    };

    const getHighlightedComponent = (initial, toHighlight, end) => {
        const highlighted = <span className={highlightClassNames}>{toHighlight}</span>;
        return (
            <React.Fragment>
                {initial}
                {highlighted}
                {end}
            </React.Fragment>
        );
    };

    const getHighlightedText = (text, isComponent) => {
        const _displayedWord = StringUtils.replaceNonAlphaNumeric(
            displayedWord.trim(),
            ' ',
            true,
        );
        const textCleaned = StringUtils.stripHtml(text);
        const replacedText = StringUtils.replaceNonAlphaNumeric(textCleaned, ' ', true);
        const regExp = new RegExp(_displayedWord, 'i');
        const match = replacedText.match(regExp);
        if (match) {
            const matchLength = match[0].length;
            const initial = textCleaned.slice(0, match.index);
            const toHighlight = textCleaned.slice(match.index, match.index + matchLength);
            const end = textCleaned.slice(match.index + matchLength, textCleaned.length);
            return isComponent
                ? getHighlightedComponent(initial, toHighlight, end)
                : getHighlightedString(initial, toHighlight, end);
        } else {
            return textCleaned;
        }
    };

    const getAllAirportsKeyId = () => {
        const { isAbTrain } = ABUtils.getABs();
        const allAirportsKeyId = isAbTrain
            ? 'flightsManager.allAirports.TRAINS'
            : 'flightsManager.allAirports.main';
        const allKey = isTrains ? 'flightsManager.allStations' : allAirportsKeyId;
        return allKey;
    };

    const shouldShowIataCode = () => {
        return !hasIATABadge && !isTrains && activeProduct !== EntryPoint.HOTELS;
    };

    const renderIataCode = (iataCode) => {
        if (!iataCode || !shouldShowIataCode()) {
            return null;
        }
        return (
            <>
                <span>{iataCode}</span>
                <span className="odf-space-outer-left-xs odf-space-outer-right-xs">-</span>
            </>
        );
    };

    const getLocationMainText = () => {
        const { displayName, name, iata, relatedLocations, type } = location;
        const locationSting = displayName || name;
        const highlightText = getHighlightedText(locationSting, true);
        const allAirportsKeyId = getAllAirportsKeyId();

        if (
            type === LocationType.CITY &&
            !isEmpty(relatedLocations) &&
            relatedLocations.length > 1
        ) {
            return (
                <>
                    {renderIataCode(iata)}
                    <span>{highlightText}</span>
                    <span className="odf-space-outer-left-xs odf-space-outer-right-xs">-</span>
                    <span><CMS id={allAirportsKeyId} /></span>
                </>
            );
        }
        return (
            <>
                {renderIataCode(iata)}
                <span>{highlightText}</span>
            </>
        );
    };

    const getRelatedLocationCityText = () => {
        const { city, country, displayName, name } = location;
        const locationString = displayName || name;
        const parentCity = parentLocation.city;
        const parentCountry = parentLocation.country;
        const cityText = city !== parentCity && city !== locationString ? city : '';
        const countryText = country !== parentCountry ? `, ${country}` : '';
        return (
            <React.Fragment>
                {cityText && (
                    <React.Fragment>
                        <span dangerouslySetInnerHTML={{ __html: `&puncsp;` }} />
                        <span className="odf-text-nowrap">
                            ({cityText}
                            {countryText})
                        </span>
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    };

    const getRelatedLocationMainText = () => {
        const { displayName, name, iata } = location;
        const locationSting = displayName || name;
        const cityText = getRelatedLocationCityText();
        return (
            <>
                {renderIataCode(iata)}
                <span>{locationSting}</span>
                {cityText}
            </>
        );
    };

    const getMainText = () => {
        const mainText = !parentLocation ? getLocationMainText() : getRelatedLocationMainText();
        return activeProduct === EntryPoint.HOTELS ? (
            <div className="odf-row">
                <div className="odf-text-ellipsis">{mainText}</div>
            </div>
        ) : (
            <>{mainText}</>
        );
    };

    const getLocationDescription = () => {
        const { city, country, region, type } = location;
        let description;
        if (type === LocationType.AIRPORT || type === LocationType.HOTEL) {
            description = `${city}, ${country}`;
        } else if (type in GeoType) {
            description = [region, country].filter(Boolean).join(', ');
        } else {
            description = `${!isEmpty(region) ? `${region}, ` : ''}${country}`;
        }
        return getHighlightedText(description, true);
    };

    const getRelatedLocationDescription = () => {
        const highlightedCity = getHighlightedText(parentLocation.city, false);
        const country = `, ${parentLocation.country}`;
        const fullLocation = highlightedCity + country;
        const distanceArg = `${location.distance} ${location.distanceUnit}`;

        return (
            <CMS
                id="airportSelector.relatedDistance"
                values={{ distance: distanceArg, city: fullLocation }}
                dangerouslySetInnerHTML
            />
        );
    };

    const getDescription = () => {
        let description;
        if (location.description) {
            description = getHighlightedText(location.description, true);
        } else if (parentLocation) {
            description = getRelatedLocationDescription();
        } else {
            description = getLocationDescription();
        }
        return description;
    };
    const isItemNested = () => {
        return parentLocation && parentLocation.type !== LocationType.NEAREST;
    };

    const isItemShowIcon = () => {
        return activeProduct === EntryPoint.HOTELS;
    };

    const _onClickItem = () => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        let label;
        if (activeProduct === EntryPoint.HOTELS) {
            label = TrackingUtils.LABEL.hotelDestinationSelected();
        } else {
            const segmentType =
                departureOrArrival === LegType.DEPARTURE ? 'departure_sel' : 'arrival_sel';
            const itemLevel =
                location.relatedLocations?.length > 1 ||
                parentLocation?.relatedLocations?.length > 1
                    ? 'all'
                    : 'only';
            const itemType = (location.geoNodeType || location.type).toLowerCase();
            const matchType =
                parentLocation && parentLocation.type === LocationType.NEAREST
                    ? 'city2'
                    : location.type.toLowerCase();
            label = `picker:${segmentType}:${itemLevel}_${itemType}_match:${matchType}`;
        }

        onClickItem(location);
        trackEvent({ action, label });
    };

    return (
        <DropdownItem
            onClick={_onClickItem}
            iconClass={`odf-icon-bidi ${getItemIcon()}`}
            isNested={isItemNested()}
            isSelected={isSelected}
            isFixedWidth={isFixedWidth}
            iataCode={hasIATABadge ? location.iata : ''}
            isShowIcon={isItemShowIcon()}
        >
            {getMainText()}
            <div className="odf-dropdown-extra">{getDescription()}</div>
        </DropdownItem>
    );
};

LocationItem.propTypes = {
    ...SourceOptionsType,
    location: LocationProp.isRequired,
    parentLocation: LocationProp,
    isSelected: PropTypes.bool.isRequired,
    departureOrArrival: LegTypeProps.isRequired,
    trackEvent: PropTypes.func.isRequired,
    activeProduct: TabTypeProp,
};

LocationItem.defaultProps = {
    parentLocation: null,
    activeProduct: TabTypePropDefault,
};

const mapStateToProps = ({ activeProduct }) => ({
    activeProduct: activeProduct.activeProduct,
});
const connected = withPageContext(LocationItem);
export { connected as LocationItem };

const ConnectedLocationItem = connect(mapStateToProps, {})(connected);
export { ConnectedLocationItem };
